import React, { useCallback, useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { RouteComponentProps, useHistory, useLocation } from "react-router-dom";

import {
  NationalResourceEntityType,
  EolasFile,
  NationalResourcesClickPayload,
  AnalyticsEvents,
} from "@eolas-medical/core";

import { useSelectFile } from "Hooks";
import { NavButton, PageTitle, InnerPageWrapper } from "UIKit";

import useSearchNationalResources from "modules/nationalResources/data/useSearchNationalResources";
import { EolasList } from "Components/EolasFileList/EolasList";
import CommunityListItem from "Pages/Knowledge/components/KnowledgeSectionItem/KnowledgeSectionItem";
import { CommunitySectionType } from "../../ClinicalCalculators/types";
import { useLocalSearch } from "shared/hooks/useLocalSearch";
import { trackEvent } from "API/Analytics";

const NationalGuidelinesBody: React.FC<RouteComponentProps> = observer(({ match: { url } }) => {
  const { onSelectFile } = useSelectFile();
  const [searchItems, setSearchItems] = useState<EolasFile[]>([]);
  const [searchInput, setSearchInput] = useState("");
  const [showSearchResult, setShowSearchResult] = useState(false);

  const history = useHistory();

  const location = useLocation();
  const { body } = (location.state as { body: string }) || {};

  const { t } = useTranslation();

  const { searchNationalResources, searchingNationalResources } = useSearchNationalResources();

  const { searchResult, onSetSearchInput } = useLocalSearch({
    data: searchItems,
    keysToSearch: ["name"],
  });

  const handleLocalSearch = useCallback(() => {
    onSetSearchInput(searchInput);
    setShowSearchResult(true);
  }, [onSetSearchInput, searchInput]);

  const handleClickGuideline = (guideline: EolasFile) => {
    trackEvent(AnalyticsEvents.NATIONAL_RESOURCE_SELECTED, {
      nationalResourceType: NationalResourceEntityType.GUIDELINE,
      resourceName: guideline.name,
    } as NationalResourcesClickPayload);
    onSelectFile(guideline);
  };

  const handleClearSearch = useCallback(() => {
    setSearchInput("");
    setShowSearchResult(false);
  }, []);

  useEffect(() => {
    searchNationalResources(
      {
        entityType: NationalResourceEntityType.GUIDELINE,
        query: body,
        mode: "searchByNationalBody",
      },
      {
        onSuccess: (response) => {
          setSearchItems(response);
        },
      },
    );
  }, [body, searchNationalResources]);

  useEffect(() => {
    if (searchInput === "") {
      setShowSearchResult(false);
    }
  }, [searchInput]);

  return (
    <>
      <InnerPageWrapper>
        <PageTitle title={t("community_nationalGuidelines_title")} />
        <NavButton onClick={() => history.goBack()} />
        <EolasList<EolasFile, "click">
          value={searchInput}
          placeholderSearchText={t("findNationalGuidelines_searchPlaceholder")}
          items={showSearchResult ? searchResult : searchItems}
          isSearchable
          searchType="click"
          isLoading={searchingNationalResources}
          onSearchInputChange={setSearchInput}
          onClearSearch={handleClearSearch}
          onClickSearch={handleLocalSearch}
          renderItem={(eolasFile) => (
            <CommunityListItem
              eolasFile={eolasFile}
              onSelectFile={handleClickGuideline}
              sectionType={CommunitySectionType.NATIONAL_RESOURCE}
            />
          )}
        />
      </InnerPageWrapper>
    </>
  );
});

export default NationalGuidelinesBody;
